import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import { ClickWordHandler } from 'components/write-speak/ZhText'
import { DiscussionAnswer } from './discussionApi'
import DiscussionList, { DiscussionThreads } from './DiscussionList'
import { CloseIcon, ReplyComposer } from './DiscussionBits'
import './discussion.css'

interface DiscussionPanelProps {
  open: boolean
  onClose: () => void
  threads: DiscussionThreads
  loading: boolean
  story: any
  activeIndex: string
  onWordTap: (answer: DiscussionAnswer) => ClickWordHandler
  /** The answer to bring into view when the panel opens. */
  focusRef: string | null
}

/**
 * The whole discussion, opened from the page preview: a side panel on desktop and a sheet on
 * phones, the same pattern as the word panel. The lesson page underneath stays as it was, with
 * Finish where it was. Only one text box is ever in view: the reply box pinned at the bottom.
 */
const DiscussionPanel: React.FC<DiscussionPanelProps> = ({ open, onClose, threads, loading, story, activeIndex, onWordTap, focusRef }) => {
  const { t } = useTranslation('discussion')
  const zhLang = useChineseLang()
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    panelRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      // A word panel open on top closes first, on its own.
      if (e.key === 'Escape' && !document.querySelector('.word_detail_modal_main_div.bounce-in-right')) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  const { discussion, replyTo } = threads
  return (
    <div className='dc-panel-wrap'>
      <div className='dc-scrim' onClick={onClose} aria-hidden='true' />
      <div className='dc-panel' role='dialog' aria-modal='true' aria-labelledby='dc-panel-title' tabIndex={-1} ref={panelRef}>
        <div className='dc-handle' aria-hidden='true' />
        <div className='dc-panel-top'>
          <div>
            <h2 className='dc-panel-title' id='dc-panel-title'>
              {t('title')}
            </h2>
            {discussion?.question && (
              <div className='dc-panel-q' lang={zhLang}>
                {discussion.question}
              </div>
            )}
          </div>
          <button type='button' className='dc-close' aria-label={t('panel.close')} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className='dc-panel-scroll'>
          {!discussion && loading && <p className='dc-muted'>{t('loading')}</p>}
          {!discussion && !loading && <p className='dc-muted'>{t('panel.loadFailed')}</p>}
          {discussion && (
            <DiscussionList
              threads={threads}
              story={story}
              activeIndex={activeIndex}
              onWordTap={onWordTap}
              scrollToRef={focusRef}
            />
          )}
        </div>
        {replyTo && (
          <div className='dc-panel-foot'>
            <ReplyComposer
              toName={replyTo.mine ? t('composer.yourAnswer') : replyTo.name}
              value={threads.draft}
              onChange={threads.setDraft}
              onCancel={threads.cancelReply}
              onSend={threads.send}
              sending={threads.sending}
              error={threads.error}
              autoFocus
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default DiscussionPanel
